import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users2, Flame, TrendingUp, Target } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, scoreColor } from "../../lib/adminApi";

const SOURCE_COLORS = ["#8cb62c", "#8B5CF6", "#22C55E", "#06B6D4", "#EAB308"];
const SOURCE_LABEL = { quote_form: "Formulario", catalog_download: "Catálogo", popup: "Pop-up" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ov, leads] = await Promise.all([
          adminHttp.get("/admin/stats/overview"),
          adminHttp.get("/admin/leads?limit=10"),
        ]);
        setStats(ov.data);
        setRecent(leads.data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) {
    return <div className="p-8 text-[#6B7280] font-mono text-xs uppercase tracking-widest">Cargando…</div>;
  }

  const statusData = STATUS_ORDER.map((s) => ({
    name: STATUS_LABELS[s], key: s, count: stats.by_status[s] || 0, fill: STATUS_COLORS[s],
  }));
  const sourceData = Object.entries(stats.by_source).map(([k, v], i) => ({
    name: SOURCE_LABEL[k] || k, value: v, fill: SOURCE_COLORS[i % SOURCE_COLORS.length],
  }));
  const timelineData = stats.leads_per_day_last_30d.map((d) => ({
    date: d.date,
    label: format(parseISO(d.date), "d MMM", { locale: es }),
    count: d.count,
  }));

  const kpis = [
    { label: "Leads totales", value: stats.total_leads, icon: Users2, accent: "#8cb62c" },
    { label: "Nuevos últimos 7d", value: stats.new_last_7d, icon: Flame, accent: "#EAB308" },
    { label: "Tasa de conversión", value: `${stats.conversion_rate}%`, icon: TrendingUp, accent: "#22C55E" },
    { label: "Score promedio", value: stats.avg_score, icon: Target, accent: "#06B6D4" },
  ];

  return (
    <div className="p-6 lg:p-10 space-y-10" data-testid="admin-dashboard">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="divider-accent" /><span className="kicker">Panel</span>
        </div>
        <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">
          Resumen <span className="text-accent">operativo</span>
        </h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="bg-white border border-[#DDE2D5] p-6 hover-lift" data-testid={`kpi-${i}`}>
              <div className="flex items-start justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{k.label}</p>
                <Icon className="w-5 h-5" style={{ color: k.accent }} />
              </div>
              <p className="mt-6 font-display font-black text-4xl md:text-5xl tracking-tight text-[#1F2937]">{k.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#DDE2D5] p-6">
          <h3 className="font-display font-black text-lg mb-4">Leads por día (últimos 30 días)</h3>
          <div style={{ width: "100%", height: 256, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timelineData}>
                <CartesianGrid stroke="#EAECEA" />
                <XAxis dataKey="label" stroke="#6B7280" style={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} stroke="#6B7280" style={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #8cb62c", color: "#1F2937" }} />
                <Line type="monotone" dataKey="count" stroke="#8cb62c" strokeWidth={3} dot={{ fill: "#8cb62c", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#DDE2D5] p-6">
          <h3 className="font-display font-black text-lg mb-4">Origen de leads</h3>
          <div style={{ width: "100%", height: 256, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {sourceData.map((s, i) => <Cell key={i} fill={s.fill} stroke="#FFFFFF" />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11, color: "#1F2937" }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #8cb62c", color: "#1F2937" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DDE2D5] p-6">
          <h3 className="font-display font-black text-lg mb-4">Leads por status</h3>
          <div style={{ width: "100%", height: 256, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <CartesianGrid stroke="#EAECEA" />
                <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} stroke="#6B7280" style={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #8cb62c", color: "#1F2937" }} />
                <Bar dataKey="count" radius={[0, 0, 0, 0]}>
                  {statusData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#DDE2D5] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black text-lg">Últimos leads</h3>
            <Link to="/admin/leads" className="font-mono text-[10px] uppercase tracking-widest text-accent hover:underline">
              Ver todos →
            </Link>
          </div>
          <div className="space-y-2" data-testid="dashboard-recent-leads">
            {recent.length === 0 && <p className="text-[#6B7280] text-sm">Sin leads aún.</p>}
            {recent.map((l) => (
              <Link key={l.id} to={`/admin/leads/${l.id}`} className="flex items-center justify-between px-3 py-3 bg-[#F2F4ED] hover:bg-[#EAECEA] border border-[#EAECEA] hover:border-accent transition-colors">
                <div className="min-w-0">
                  <p className="font-display font-bold text-sm text-[#1F2937] truncate">{l.full_name || l.email}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280] truncate">
                    {SOURCE_LABEL[l.source] || l.source} · {l.city || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className="font-mono text-[10px] uppercase px-2 py-1 tracking-wider"
                    style={{ background: STATUS_COLORS[l.status || "new"], color: "#1F2937" }}
                  >
                    {STATUS_LABELS[l.status || "new"]}
                  </span>
                  <span className="font-display font-black text-lg tabular-nums" style={{ color: scoreColor(l.score || 0) }}>
                    {l.score || 0}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[#6B7280]" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
